const CSRF_COOKIE_NAME = "fyp_csrf"
const CSRF_HEADER_NAME = "X-CSRF-Token"

/**
 * Returns the latest CSRF token from cookies.
 *
 * @returns The CSRF cookie value, or `null` when the cookie is missing.
 */
export const getCsrfTokenFromCookie = (): string | null => {
  if (typeof document === "undefined") {
    return null
  }

  const cookies = document.cookie.split(";")

  for (const cookie of cookies) {
    const trimmedCookie = cookie.trim()

    if (trimmedCookie.startsWith(`${CSRF_COOKIE_NAME}=`)) {
      return decodeURIComponent(trimmedCookie.slice(CSRF_COOKIE_NAME.length + 1))
    }
  }

  return null
}

const isUnsafeMethod = (method: string): boolean => {
  const normalizedMethod = method.toUpperCase()
  return (
    normalizedMethod === "POST" ||
    normalizedMethod === "PUT" ||
    normalizedMethod === "PATCH" ||
    normalizedMethod === "DELETE"
  )
}

const hasHeader = (headers: Headers, name: string): boolean => {
  const value = headers.get(name)
  return typeof value === "string" && value.trim().length > 0
}

/**
 * Attaches CSRF header for cookie-authenticated unsafe requests.
 *
 * @param method - HTTP method for the outgoing request.
 * @param headers - Request headers to mutate.
 */
export const maybeAttachCsrfHeader = (method: string, headers: Headers): void => {
  if (!isUnsafeMethod(method)) {
    return
  }

  if (hasHeader(headers, "Authorization") || hasHeader(headers, "X-API-Key")) {
    return
  }

  const csrfToken = getCsrfTokenFromCookie()

  if (!csrfToken) {
    return
  }

  headers.set(CSRF_HEADER_NAME, csrfToken)
}

export const csrfHeaderName = CSRF_HEADER_NAME
